import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowRight, Code, Wrench, Zap, Monitor, Mail, Linkedin, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { personalInfo, skills, projects, services } from '../mock';
import PremiumCalculator from './PremiumCalculator';
import ProjectDemoModal from './ProjectDemoModal';
import { HomeSeo } from '../seo/PageSeo';
import { Link } from 'react-router-dom';
import { caseStudyById, casePath, legalPath } from '../seo/seoConfig';
import { scrollToId } from '../hooks/useSectionNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  code: Code,
  wrench: Wrench,
  zap: Zap,
  monitor: Monitor
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const { lang } = useParams();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) setTimeout(() => scrollToId(hash.slice(1)), 100);
  }, [hash]);

  // Translation mapping for dynamic content
  const projectTranslationKeys = {
    "EmployeeHub": "projectData.employeeHub",
    "EmployeeHub Mini": "projectData.employeeHubMini",
    "KosovAI": "projectData.kosovAI",
    "Restaurant Point App": "projectData.restaurantPoint",
    "Vehicle Shop App": "projectData.vehicleShop",
    "Banking App": "projectData.banking"
  };

  const serviceTranslationKeys = {
    "Custom Business Apps": "servicesData.customApps",
    "Internal Tools for Companies": "servicesData.internalTools",
    "Process Automation": "servicesData.processAutomation",
    "Web & Desktop Solutions": "servicesData.webDesktop"
  };

  const getTranslatedProject = (project) => {
    const key = projectTranslationKeys[project.title];
    if (key) {
      return {
        ...project,
        title: t(`${key}.title`),
        description: t(`${key}.description`)
      };
    }
    return project;
  };

  const getTranslatedService = (service) => {
    const key = serviceTranslationKeys[service.title];
    if (key) {
      return {
        ...service,
        title: t(`${key}.title`),
        description: t(`${key}.description`)
      };
    }
    return service;
  };

  const scrollToSection = scrollToId;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, { ...formData, language: (i18n.language || 'en').slice(0, 2) });
      
      if (response.data.success) {
        toast({
          title: t('contact.successTitle'),
          description: t('contact.success'),
        });
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      let errorMessage = 'Failed to send message. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation error - extract message from first error
          errorMessage = detail[0].msg || detail[0].message || errorMessage;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      setSubmitStatus({ error: errorMessage });
      toast({
        title: t('contact.errorTitle'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-slate-950 dark:to-slate-900">
      <HomeSeo lang={lang} />
      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
              <span className="text-blue-600 dark:text-blue-400 font-medium">
                {t('hero.location')}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              {t('hero.headline')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-10 max-w-3xl mx-auto">
              {t('hero.subheadline')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => scrollToSection('projects')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg group shadow-lg hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                {t('hero.viewProjects')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('contact')}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t('hero.contactMe')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t("projects.title")}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('projects.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.slice(0, 4).map((project) => {
              const translatedProject = getTranslatedProject(project);
              return (
                <Card
                  key={project.id}
                  onClick={() => setActiveProject(translatedProject)}
                  data-testid={`project-card-${project.id}`}
                  className="group cursor-pointer hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 overflow-hidden border-2 hover:border-blue-500/50 transform hover:-translate-y-2"
                >
                  <div className="relative h-48 overflow-hidden bg-gray-200 dark:bg-gray-800">
                    <img
                      src={translatedProject.image}
                      alt={translatedProject.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="px-4 py-2 bg-white/95 dark:bg-slate-900/95 text-blue-600 dark:text-blue-400 rounded-full text-sm font-bold shadow-xl flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                        {t('projects.viewLivePreview')}
                      </span>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl">{translatedProject.title}</CardTitle>
                    <CardDescription className="text-base">{translatedProject.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {translatedProject.techStack.map((tech) => (
                        <Badge key={tech} variant="secondary">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      onClick={(e) => { e.stopPropagation(); setActiveProject(translatedProject); }}
                      variant="outline"
                      data-testid={`view-demo-${project.id}`}
                      className="w-full group/btn"
                    >
                      {t("projects.viewDemo")}
                      <ExternalLink className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                    <Link
                      to={casePath(lang, caseStudyById(project.id).slug)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`case-study-link-${project.id}`}
                      className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {t('caseStudy.link')} <ArrowRight className="h-3 w-3" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <ProjectDemoModal
          project={activeProject}
          isOpen={!!activeProject}
          onClose={() => setActiveProject(null)}
        />
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t('services.title')}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              const translatedService = getTranslatedService(service);
              return (
                <Card key={service.id} className="group hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 hover:-translate-y-2 border-2 hover:border-blue-500/50 cursor-pointer">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:scale-110 transition-all duration-300 shadow-lg">
                      <IconComponent className="h-6 w-6 text-blue-600 dark:text-blue-400 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-xl">{translatedService.title}</CardTitle>
                    <CardDescription className="text-sm">{translatedService.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price Calculator Section */}
      <PremiumCalculator />

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t('about.title')}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {t("about.name")}
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                {t("about.bio")}
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={`mailto:${personalInfo.email}`}>
                  <Button variant="outline" className="gap-2">
                    <Mail className="h-4 w-4" />
                    {t("about.emailMe")}
                  </Button>
                </a>
                <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                  </Button>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t("about.skillsTitle")}
              </h4>
              <div className="space-y-4">
                {skills.map((skill) => (
                  <div key={skill.name}>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {skill.name}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {skill.level}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t('contact.title')}
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('contact.subtitle')}
            </p>
          </div>

          <Card className="shadow-2xl border-2 hover:border-blue-500/50 transition-all duration-300">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} method="post" action="#contact" className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("contact.name")} *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t("contact.namePlaceholder")}
                      className="transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("contact.email")} *</Label>
                    <Input
                      id="email"
                      name="email"
                      data-testid="contact-email-input"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t("contact.emailPlaceholder")}
                      className="transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">{t("contact.company")}</Label>
                  <Input
                    id="company"
                    name="company"
                    data-testid="contact-company-input"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder={t("contact.companyPlaceholder")}
                    className="transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t("contact.message")} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t("contact.messagePlaceholder")}
                    rows={6}
                    className="transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  data-testid="contact-submit-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.sending') : t('contact.send')}
                </Button>
                {submitStatus === 'success' && (
                  <p role="status" className="flex items-start gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3" data-testid="contact-success-message">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> {t('contact.successInline')}
                  </p>
                )}
                {submitStatus?.error && (
                  <p role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3" data-testid="contact-error-message">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {t('contact.errorInline')}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {t('contact.privacyNote')}{' '}
                  <Link to={legalPath(lang, 'privacy')} className="underline hover:text-blue-600" data-testid="contact-privacy-link">{t('footer.privacy')}</Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;